import type { User, Vendor } from '@keboola/api-client/developerPortal/types';

/**
 * The vendor-filter option for users referencing a vendor id with no record.
 *
 * - Not "awaiting approval": `GET /admin/vendors` includes unapproved vendors.
 * - Matches vendors renamed on approval or deleted; the label is what admins know it by.
 */
export const PENDING_VENDORS_OPTION = '...';

/**
 * No account-status filter: `/admin/users` returns no status field.
 */
export type AdminUserFilters = {
  /** Substring over name and email. */
  query: string;
  /** A vendor id, {@link PENDING_VENDORS_OPTION}, or `''` for all. */
  vendor: string;
};

const contains = (haystack: string | null | undefined, needle: string): boolean =>
  (haystack ?? '').toLowerCase().includes(needle);

/**
 * Alphabetical by display name.
 *
 * - Returns a new array — sorting in place would mutate the query cache.
 */
export const sortAdminUsers = (users: readonly User[]): User[] =>
  [...users].sort((a, b) => (a.name ?? a.username).localeCompare(b.name ?? b.username));

/** Whether a user references a vendor id with no record — renamed on approval, or deleted. */
const hasPendingVendor = (user: User, knownVendorIds: ReadonlySet<string>): boolean =>
  (user.vendors ?? []).some((id) => !knownVendorIds.has(id));

/**
 * Applies the two admin list filters, then sorts.
 *
 * - The vendor filter has a third mode — see {@link PENDING_VENDORS_OPTION}.
 */
export const filterAdminUsers = (
  users: readonly User[],
  vendors: readonly Vendor[],
  { query, vendor }: AdminUserFilters,
): User[] => {
  const knownVendorIds = new Set(vendors.map((v) => v.id));
  const needle = query.trim().toLowerCase();

  const matched = users.filter((user) => {
    if (vendor !== '') {
      const matchesVendor =
        vendor === PENDING_VENDORS_OPTION
          ? hasPendingVendor(user, knownVendorIds)
          : (user.vendors ?? []).includes(vendor);
      if (!matchesVendor) return false;
    }

    if (needle !== '') {
      return contains(user.username, needle) || contains(user.name, needle);
    }

    return true;
  });

  return sortAdminUsers(matched);
};

/** A vendor's display name, falling back to the raw id — an orphaned reference has no vendor record. */
export const vendorNameById = (id: string, vendors: readonly Vendor[]): string =>
  vendors.find((vendor) => vendor.id === id)?.name ?? id;
